import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  HeroRoot,
  ReferralHeaderBar,
  LogoCluster,
  PgLogoWrap,
  PartnershipPlus,
  PartnerHeaderLogo,
  HeaderCtaWrap,
  HeaderSentinel,
  HeaderSpacer,
  HeaderGetQuotesButton,
  HeaderCtaContent,
  HeroSection,
  HeroContainer,
  HeroMediaFrame,
  HeroImage,
  HeroOverlayCard,
  HeroCardInner,
  HeroTextColumn,
  HeroSubtitle,
  HeroCtaPanel,
  ReferralHeaderBarInner,
} from './styles';
import { ReferralDesktopContainer } from '../styles';
import { Events, track } from 'utils/analytics';
import { Props } from './types';
import PgLogo from 'components/PgIcons/PgLogo';
import CtaArrowIcon from './CtaArrowIcon';
import HeroQuotesCta from './HeroQuotesCta';
import HeroTitleContent from './HeroTitleContent';
import HeroBackgroundLines from './HeroBackgroundLines';
import { DEFAULT_HERO_BACKGROUND, heroBackgroundImageStyle } from './constants';

const HeroOverlayBody: React.FC<{
  onOpenQuotes: () => void;
  subtext?: string | null;
  title: string;
}> = ({ onOpenQuotes, subtext, title }) => (
  <HeroCardInner>
    <HeroTextColumn>
      <HeroTitleContent title={title} />
      {subtext ? <HeroSubtitle>{subtext}</HeroSubtitle> : null}
    </HeroTextColumn>
    <HeroCtaPanel>
      <HeroQuotesCta onClick={onOpenQuotes} />
    </HeroCtaPanel>
  </HeroCardInner>
);

const HEADER_QUOTES_CTA_LABEL = 'Get a Quote';

const HeaderQuotesCta: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const handleClick = () => {
    track(Events.CLICKED_BUTTON, { analyticsText: HEADER_QUOTES_CTA_LABEL });
    onClick();
  };

  return (
    <HeaderGetQuotesButton type="button" onClick={handleClick}>
      <HeaderCtaContent>
        {HEADER_QUOTES_CTA_LABEL}
        <CtaArrowIcon />
      </HeaderCtaContent>
    </HeaderGetQuotesButton>
  );
};

const Hero: React.FunctionComponent<Props> = ({
  agentUUID,
  imageURL,
  customHeroImage,
  subtext,
  title,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const hasCustomHeroImage = Boolean(customHeroImage?.trim());

  const heroBackgroundUrl = hasCustomHeroImage ? customHeroImage!.trim() : DEFAULT_HERO_BACKGROUND;

  const getQuotesURL = `${process.env.REACT_APP_LIFE_WEB_FLOW_URL}?agent_uuid=${agentUUID}`;

  const openQuotesForm = useCallback(() => {
    window.open(getQuotesURL, '_blank')?.focus();
  }, [getQuotesURL]);

  const measureHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useLayoutEffect(() => {
    measureHeaderHeight();
    window.addEventListener('resize', measureHeaderHeight);

    return () => window.removeEventListener('resize', measureHeaderHeight);
  }, [measureHeaderHeight]);

  useEffect(() => {
    const el = sentinelRef.current;

    if (!el || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderSticky(!entry.isIntersecting);
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (isHeaderSticky) {
      measureHeaderHeight();
    }
  }, [isHeaderSticky, measureHeaderHeight]);

  return (
    <HeroRoot>
      <HeaderSentinel ref={sentinelRef} aria-hidden />
      {isHeaderSticky && <HeaderSpacer $height={headerHeight} />}
      <ReferralHeaderBar
        ref={headerRef}
        $isSticky={isHeaderSticky}
        aria-label="Policygenius and partner"
      >
        <ReferralHeaderBarInner>
          <LogoCluster>
            <PgLogoWrap>
              <PgLogo />
            </PgLogoWrap>
            <PartnershipPlus aria-hidden="true">+</PartnershipPlus>
            {imageURL ? <PartnerHeaderLogo src={imageURL} alt="" /> : null}
          </LogoCluster>
          <HeaderCtaWrap $visible={isHeaderSticky}>
            <HeaderQuotesCta onClick={openQuotesForm} />
          </HeaderCtaWrap>
        </ReferralHeaderBarInner>
      </ReferralHeaderBar>

      <HeroSection aria-label="Hero">
        <ReferralDesktopContainer>
          <HeroContainer>
            <HeroMediaFrame>
              <HeroImage
                style={heroBackgroundImageStyle(heroBackgroundUrl)}
                data-testid="referral-hero-background"
                aria-hidden
              >
                {!hasCustomHeroImage && <HeroBackgroundLines />}
              </HeroImage>
              <HeroOverlayCard>
                <HeroOverlayBody onOpenQuotes={openQuotesForm} subtext={subtext} title={title} />
              </HeroOverlayCard>
            </HeroMediaFrame>
          </HeroContainer>
        </ReferralDesktopContainer>
      </HeroSection>
    </HeroRoot>
  );
};

export default Hero;
